import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Alert,
  Typography,
  Form,
  message,
  Row,
  Col,
  Select,
  Button,
  Modal,
  Empty,
  Radio,
  Layout,
  Grid,
  Spin,
} from "antd";
import {
  InfoCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import AllApplications from "./applications";
import { downloadCSV } from "../user/csvDownloader";
import useLogout from "../useLogout";
import "../roleBasedTester/RoleBasedAddOTP.css";

const COUNTRY_PREFIXES = {
  "233": "Ghana",
  "223": "Mali",
  "224": "Guinea",
};

const COUNTRY_ORDER = ["Ghana", "Mali", "Guinea", "Other"];

const NUMBERS_PER_COLUMN = 50;

const groupNumbersByCountry = (numbers) => {
  const groups = { Ghana: [], Mali: [], Guinea: [], Other: [] };
  numbers.forEach((num) => {
    const str = String(num);
    const prefix = str.slice(0, 3);
    const country = COUNTRY_PREFIXES[prefix] || "Other";
    groups[country].push(num);
  });
  return groups;
};

const NumbersByCountry = ({ groups }) => (
  <>
    {COUNTRY_ORDER.map((country) => {
      const nums = groups[country] || [];
      if (nums.length === 0) return null;

      const columns = [];
      for (let i = 0; i < nums.length; i += NUMBERS_PER_COLUMN) {
        columns.push(nums.slice(i, i + NUMBERS_PER_COLUMN));
      }

      return (
        <div key={country} className="rbotp-country-section">
          <div className="rbotp-country-header">
            <span className="rbotp-country-name">{country}</span>
            <span className="rbotp-country-badge">{nums.length}</span>
            <Button
              size="small"
              onClick={() => {
                const text = nums.join("\n");
                navigator.clipboard.writeText(text).then(
                  () =>
                    message.success(
                      `${country}: ${nums.length} numbers copied to clipboard`
                    ),
                  () => message.error("Failed to copy")
                );
              }}
              className="rbotp-copy-btn"
            >
              Copy All
            </Button>
          </div>
          <div className="rbotp-numbers-columns">
            {columns.map((column, colIndex) => (
              <div
                key={`${country}-col-${colIndex}`}
                className="rbotp-numbers-column"
              >
                {column.map((number, index) => (
                  <div
                    key={`${country}-${colIndex}-${index}`}
                    className="rbotp-number-item"
                  >
                    {number}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    })}
  </>
);

const { Title } = Typography;
const { Option } = Select;
const { Content } = Layout;
const { useBreakpoint } = Grid;

const RunTest = () => {
  const logout = useLogout();
  const mnoAPI = process.env.REACT_APP_API_MobileOperators;
  const simApiUrl = process.env.REACT_APP_API_SIMForSpecialTesters;
  const checkIfNumsAssigned =
    "https://tywp44yzhi.execute-api.us-east-1.amazonaws.com/dev/sims_for_special_testers";
  const specialCountriesAPI =
    "https://0id4y5c0xb.execute-api.us-east-1.amazonaws.com/dev";

  const [messageApi, contextHolder] = message.useMessage();
  const [mnoNameList, setMNONameList] = useState([]);
  const [mnoObject, setMnoObject] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [fetchingMoreNumbers, setFetchingMoreNumbers] = useState(false);
  const [isSpecialUser, setIsSpecialUser] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [showNumbersCard, setShowNumbersCard] = useState(false);
  const [numbersAssigned, setNumbersAssigned] = useState(false);
  const [lockNumbersModalVisible, setLockNumbersModalVisible] = useState(false);
  const [noNumbersAvailable, setNoNumbersAvailable] = useState(false);
  const [numbersLocked, setNumbersLocked] = useState(false);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [showLockMoreAlert, setShowLockMoreAlert] = useState(true);
  const [disabledCountries, setDisabledCountries] = useState({});
  const [simLimit, setSimLimit] = useState(null);
  const [stSimLimit, setStSimLimit] = useState(null);
  const [stAddSimLimit, setStAddSimLimit] = useState(250);

  const storedUser = localStorage.getItem("user");
  const user = JSON.parse(storedUser);
  const screens = useBreakpoint();

  // Hardcode country to Mali
  const country = "Mali";
  const countryId = 146;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${checkIfNumsAssigned}/${user.userId}`
        );

        if (response.data.inactive) {
          logout();
        } else if (response.data.body && response.data.body.length >= 100) {
          if (response.data.body.length === 600) {
            console.log("length:", response.data.body.length);
            setShowLockMoreAlert(false);
          }
          setNumbersAssigned(true);
          setPhoneNumbers(response.data.body);
          const phoneNumbers = response.data.body.map((item) => item.msisdn);
          setNumbers(phoneNumbers);
          if (user.userId === 219) {
            setNumbersLocked(true);
          }
        } else if (response.data.body && response.data.body.length === 50) {
          // Make call to the special countries API
          const specialCountriesResponse = await axios.get(
            `${specialCountriesAPI}/${user.userId}`
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchData();
  }, [fetchingMoreNumbers]);

  useEffect(() => {
    if (user.userId === 219) {
      setIsSpecialUser(true);
    }
    const fetchData = async () => {
      try {
        setLoadingCountries(true);
        // Fetch MNO names with hardcoded countryId for Mali
        const dataUrl = `${mnoAPI}/${countryId}`;
        const response = await axios.get(dataUrl);

        setMNONameList(response.data.body.rows);
        setStSimLimit(response.data.body.simLimit.simLimit);
        // setStSimLimit(150);
        // setStAddSimLimit(response.data.body.simLimit.addSimLimit);

        // const limitAPI = `${mnoAPI}/?country=${countryId}`;
        // const response = await axios.get(dataUrl);
      } catch (error) {
        console.error("Error fetching MNO names:", error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchData();
  }, []);

  const handleOperatorChange = (value) => {
    const selectedMNO = mnoNameList.find((mno) => mno.id === value);
    setMnoObject(selectedMNO);
    setShowNumbersCard(false); // Hide numbers card initially
  };

  const fetchPhoneNumbers = async (mnoId, simLimit) => {
    setLoadingNumbers(true);
    try {
      const response = await axios.get(
        `${simApiUrl}?user_id=${user.userId}`
      );
      // console.log("response from fetch number api", response.data.body);

      if (response.data.body.length === 0) {
        // console.log("empty body");
        setNoNumbersAvailable(true);
      } else {
        setNoNumbersAvailable(false);
      }

      const phoneNumbers = response.data.body.map(
        (phoneNumber) => phoneNumber.msisdn
      );
      setNumbers(phoneNumbers);

      const numbers = response.data.body.map((phoneNumber) => phoneNumber.id);
      setSelectedNumbers(numbers);
      setShowNumbersCard(true); // Show numbers card after numbers are fetched
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
    } finally {
      setLoadingNumbers(false);
    }
  };

  const handleSimLimitChange = (e) => {
    const limit = e.target.value;
    setSimLimit(limit);
    fetchPhoneNumbers(mnoObject.id, limit); // Fetch phone numbers as soon as SIM limit is selected
  };

  const handleLockNumbersClick = async () => {
    try {
      if (!isSpecialUser && !disabledCountries[country]) {
        setDisabledCountries((prev) => ({ ...prev, [country]: true }));
        await axios.post(`${simApiUrl}?lock=true`, {
          user_id: user.userId,
          sims_id: selectedNumbers,
        });
        setNumbersLocked(true);
      }
      if (user.userId === 219) {
        downloadCSV(numbers);
      } else {
        setLockNumbersModalVisible(true);
      }
    } catch (error) {
      console.error("Error locking numbers:", error);
    }
  };
  const handleLockMoreNumbers = async () => {
    try {
      setFetchingMoreNumbers(true);
      setLoadingNumbers(true); // Start loading indicator
      setNoNumbersAvailable(false); // Reset "no numbers" flag
      const checkLimitResponse = await axios.get(
        `https://lur5gqyw0d.execute-api.us-east-1.amazonaws.com/dev/${user.userId}`
      );

      if (checkLimitResponse.data.result === "true") {
        message.success(
          `${checkLimitResponse.data.sims_limit} more numbers locked successfully.`
        );
        setFetchingMoreNumbers(false);
      } else {
        setFetchingMoreNumbers(false);

        message.error(
          `You cannot lock more numbers unless you generate ${checkLimitResponse.data.otp_limit} OTPs for already locked numbers`
        );
      }
    } catch (error) {
      console.error("Error locking more numbers:", error);
      message.error("An error occurred while locking more numbers.");
    } finally {
      setLoadingNumbers(false); // Stop loading indicator
    }
  };

  const handleDownloadClick = () => {
    downloadCSV(numbers);
  };

  const handleSelectApplicationsClick = () => {
    setLockNumbersModalVisible(true);
  };

  return (
    <Layout>
      {contextHolder}
      <Content
        style={{ padding: "0 18px", minHeight: "100vh", background: "#fff" }}
      >
        {numbersAssigned ? (
          <>
            <div className="rbotp">
              {showLockMoreAlert && (
                <div className="rbotp-lock-alert">
                  <InfoCircleOutlined className="rbotp-lock-alert-icon" />
                  <span className="rbotp-lock-alert-text">
                    Are you sure that you have used all the assigned numbers? If
                    so, click on the button to lock additional numbers.
                  </span>
                  <Button
                    type="primary"
                    size="middle"
                    loading={fetchingMoreNumbers}
                    onClick={handleLockMoreNumbers}
                    className="rbotp-btn-primary"
                  >
                    Lock More Numbers
                  </Button>
                </div>
              )}

              <div className="rbotp-card">
                <div className="rbotp-card-header">
                  <h2 className="rbotp-card-title">
                    Numbers Assigned To You for Today
                  </h2>
                  <div className="rbotp-actions">
                    <Button
                      type="primary"
                      size="middle"
                      icon={isSpecialUser ? <DownloadOutlined /> : undefined}
                      onClick={
                        isSpecialUser && numbersLocked
                          ? handleDownloadClick
                          : handleSelectApplicationsClick
                      }
                      disabled={disabledCountries[country]}
                      className={
                        isSpecialUser ? "rbotp-btn-secondary" : "rbotp-btn-primary"
                      }
                    >
                      {isSpecialUser && numbersLocked
                        ? "Download CSV"
                        : isSpecialUser
                        ? "Download CSV"
                        : "Select Applications"}
                    </Button>
                  </div>
                </div>
                <div className="rbotp-card-body">
                  <NumbersByCountry
                    groups={groupNumbersByCountry(numbers)}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="rbotp">
            {!numbersAssigned && (
              <>
                {!isSpecialUser && (
                  <Alert
                    message="Alert: If you don't click on Lock Numbers button, the numbers will not be locked for you and you will not be able to see the generated OTPs. Therefore make sure to click on Lock Numbers."
                    type="error"
                    banner
                  />
                )}
                <Title level={4}>Numbers Assignment</Title>
                <Row gutter={16}>
                  <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                    <Form.Item
                      label="MNO Name"
                      required
                      tooltip={{
                        title: "This is a required field",
                        icon: <InfoCircleOutlined />,
                      }}
                    >
                      <Select
                        showSearch
                        placeholder="Select MNO"
                        optionFilterProp="children"
                        onChange={handleOperatorChange}
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        disabled={loadingCountries}
                      >
                        {mnoNameList.map((mno) => (
                          <Option key={mno.id} value={mno.id}>
                            {mno.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                {mnoObject && (
                  <Row gutter={16}>
                    <Col xs={{ span: 24 }}>
                      <Form.Item
                        label="Number of SIMs"
                        required
                        tooltip={{
                          title: "This is a required field",
                          icon: <InfoCircleOutlined />,
                        }}
                      >
                        <Radio.Group
                          onChange={handleSimLimitChange}
                          value={simLimit}
                          disabled={showNumbersCard}
                        >
                          <Radio value={stSimLimit}>{stSimLimit}</Radio>
                          {/* <Radio value={1000}>1000</Radio> */}
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                )}
                {loadingNumbers && (
                  <Spin tip="Loading phone numbers..." size="large" />
                )}
                {!loadingNumbers && showNumbersCard && (
                  <>
                    {noNumbersAvailable ? (
                      <Empty
                        description="No numbers available for the selected MNO and SIM limit"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    ) : (
                      <>
                        {numbers.length > 0 && (
                          <>
                            {showNumbersCard && (
                              <>
                                {loadingNumbers ? (
                                  <Spin tip="Loading...">
                                    <div
                                      className="rbotp-card"
                                      style={{ minHeight: 120 }}
                                    />
                                  </Spin>
                                ) : (
                                  <div className="rbotp-card">
                                    <div className="rbotp-card-header">
                                      <h2 className="rbotp-card-title">
                                        Numbers Assigned To You for Today
                                      </h2>
                                      <div className="rbotp-actions">
                                        <Button
                                          type="primary"
                                          size="middle"
                                          icon={
                                            isSpecialUser && numbersLocked ? (
                                              <DownloadOutlined />
                                            ) : undefined
                                          }
                                          className={
                                            isSpecialUser && numbersLocked
                                              ? "rbotp-btn-secondary"
                                              : "rbotp-btn-primary"
                                          }
                                          onClick={
                                            isSpecialUser && numbersLocked
                                              ? handleDownloadClick
                                              : handleLockNumbersClick
                                          }
                                          disabled={disabledCountries[country]}
                                        >
                                          {isSpecialUser && numbersLocked
                                            ? "Download CSV"
                                            : "Lock Numbers"}
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="rbotp-card-body">
                                      {noNumbersAvailable ? (
                                        <Empty
                                          description={
                                            <span>
                                              <strong>
                                                No numbers available for today.
                                              </strong>
                                            </span>
                                          }
                                        />
                                      ) : (
                                        <NumbersByCountry
                                          groups={groupNumbersByCountry(
                                            numbers
                                          )}
                                        />
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                            <br />
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </Content>
      <Modal
        title="Select Applications"
        open={lockNumbersModalVisible}
        width={"50%"}
        onCancel={() => setLockNumbersModalVisible(false)}
        footer={null}
      >
        <AllApplications numbers={numbers} />
      </Modal>
    </Layout>
  );
};

export default RunTest;
